/**
 * Fibre-G Precision Science Protocol Calculator
 * 100% Exact Algorithmic Parity with frontend/src/services/scienceCalculator.ts & ResultsSection.tsx
 */
window.FibregCalculator = (function () {
  'use strict';

  const ACTIVITY_MULTIPLIER = {
    desk: 1.2,
    mixed: 1.375,
    standing: 1.55,
    manual: 1.725,
  };

  const GOAL_CALORIE_MULTIPLIER = {
    'weight-loss': 0.8,
    maintenance: 1.0,
    'muscle-gain': 1.1,
    recovery: 1.05,
  };

  const PROTEIN_PER_KG = {
    'weight-loss': 2.0,
    maintenance: 1.6,
    'muscle-gain': 2.2,
    recovery: 1.8,
  };

  const MEAL_SCORE = {
    light: 0.5,
    normal: 0.8,
    heavy: 0.4,
  };

  function round(v, d) {
    d = d !== undefined ? d : 1;
    const f = Math.pow(10, d);
    return Math.round(v * f) / f;
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function computeLocalProtocol(input) {
    const isMale = input.gender === 'male';
    const heightM = input.height / 100;

    // Tier 1 — Core Body Metrics
    const bmi = round(input.weight / (heightM * heightM), 1);
    let bmiClassification;
    if (bmi < 18.5) bmiClassification = 'Underweight';
    else if (bmi < 25) bmiClassification = 'Normal weight';
    else if (bmi < 30) bmiClassification = 'Overweight';
    else bmiClassification = 'Obese';

    const heightInches = input.height / 2.54;
    const idealBodyWeight = round(
      isMale ? 50 + 2.3 * (heightInches - 60) : 45.5 + 2.3 * (heightInches - 60),
      1
    );

    const bmr = round(
      isMale
        ? 10 * input.weight + 6.25 * input.height - 5 * input.age + 5
        : 10 * input.weight + 6.25 * input.height - 5 * input.age - 161,
      0
    );

    const tier1 = { bmi, bmiClassification, idealBodyWeight, bmr };

    // Tier 2 — Energy & Fiber Targets
    const tdee = round(tier1.bmr * (ACTIVITY_MULTIPLIER[input.activityLevel] || 1.2), 0);
    const calorieTarget = round(tdee * (GOAL_CALORIE_MULTIPLIER[input.primaryGoal] || 1.0), 0);

    const baseFiber = (calorieTarget / 1000) * 14;
    const genderAgeMin = isMale ? (input.age < 50 ? 38 : 30) : (input.age < 50 ? 25 : 21);
    const fiberTargetGrams = round(Math.max(baseFiber, genderAgeMin), 1);

    const tier2 = { tdee, calorieTarget, fiberTargetGrams };

    // Tier 3 — Derived Health Insights
    const proteinGrams = round(input.weight * (PROTEIN_PER_KG[input.primaryGoal] || 1.6), 0);
    const fatGrams = round((tier2.calorieTarget * 0.25) / 9, 0);
    const carbGrams = round((tier2.calorieTarget - proteinGrams * 4 - fatGrams * 9) / 4, 0);

    const fiberRatio = 0.3;
    const mealScore = MEAL_SCORE[input.eatingLevel] || 0.8;
    const gutHealthScore = round(clamp(fiberRatio * 60 + mealScore * 40, 0, 100), 0);

    let gutHealthLabel;
    if (gutHealthScore < 40) gutHealthLabel = 'Needs attention';
    else if (gutHealthScore < 70) gutHealthLabel = 'Moderate';
    else gutHealthLabel = 'Good';

    const assumedIntake = tier2.fiberTargetGrams * 0.4;
    const fiberDeficitGrams = round(tier2.fiberTargetGrams - assumedIntake, 1);

    const tier3 = {
      proteinGrams,
      fatGrams,
      carbGrams,
      gutHealthScore,
      gutHealthLabel,
      fiberDeficitGrams,
    };

    // Tier 4 — Condition-Aware Output
    let dose = tier3.fiberDeficitGrams * 0.5;
    const conditionsLower = (input.healthConditions || []).map(function (c) {
      return c.toLowerCase();
    });

    if (conditionsLower.some(function (c) { return c.indexOf('diabetes') !== -1; })) dose *= 1.1;
    if (conditionsLower.some(function (c) { return c.indexOf('digestive') !== -1 || c.indexOf('ibs') !== -1; })) dose *= 0.8;
    if (conditionsLower.some(function (c) { return c.indexOf('pregnancy') !== -1; })) dose *= 0.9;
    if (conditionsLower.some(function (c) { return c.indexOf('kidney') !== -1; })) dose *= 0.6;

    const fiberGServingGrams = round(clamp(dose, 5, 30), 1);

    let bmiScore;
    if (tier1.bmiClassification === 'Normal weight') bmiScore = 0;
    else if (tier1.bmiClassification === 'Overweight') bmiScore = 2;
    else if (tier1.bmiClassification === 'Obese') bmiScore = 4;
    else bmiScore = 1;

    let deficitScore;
    if (tier3.fiberDeficitGrams < 5) deficitScore = 0;
    else if (tier3.fiberDeficitGrams < 15) deficitScore = 1;
    else if (tier3.fiberDeficitGrams < 25) deficitScore = 2;
    else deficitScore = 3;

    const conditionScore = (input.healthConditions || []).length;
    const metabolicRiskScore = clamp(bmiScore + deficitScore + conditionScore, 0, 10);

    let metabolicRiskLevel;
    let metabolicRiskMessage;
    if (metabolicRiskScore <= 2) {
      metabolicRiskLevel = 'Low';
      metabolicRiskMessage = 'Good baseline health';
    } else if (metabolicRiskScore <= 5) {
      metabolicRiskLevel = 'Moderate';
      metabolicRiskMessage = 'Fiber intervention beneficial';
    } else {
      metabolicRiskLevel = 'High';
      metabolicRiskMessage = 'Consult a nutritionist';
    }

    const tier4 = {
      fiberGServingGrams,
      metabolicRiskScore,
      metabolicRiskLevel,
      metabolicRiskMessage,
    };

    const waterTarget = tier1.idealBodyWeight ? (tier1.idealBodyWeight * 0.045).toFixed(1) : '3.3';
    const aiRecommendations = [
      'Your daily fiber target is ' + tier2.fiberTargetGrams + 'g. Start with ' + fiberGServingGrams + 'g of Fiber-G and increase gradually over 2 weeks.',
      'Drink at least ' + waterTarget + 'L of water daily to support fiber digestion.',
    ];

    return {
      tier1: tier1,
      tier2: tier2,
      tier3: tier3,
      tier4: tier4,
      aiRecommendations: aiRecommendations,
    };
  }

  async function calculateProtocol(input) {
    const apiUrl = window.FibregAuth && typeof window.FibregAuth.getApiUrl === 'function'
      ? window.FibregAuth.getApiUrl()
      : ((window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') ? 'http://localhost:3001' : 'https://fibre-g-server-production.up.railway.app');
    try {
      const response = await fetch(apiUrl + '/api/protocol/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw new Error('API returned status: ' + response.status);
      }

      return await response.json();
    } catch (e) {
      console.warn('[FibregCalculator] Remote API call failed or offline, using local fallback:', e);
      return computeLocalProtocol(input);
    }
  }

  let currentInput = null;
  let currentResult = null;

  function renderResults(result, input) {
    if (input) currentInput = input;
    if (result) currentResult = result;

    // Cache active protocol in session storage to preserve across login flow
    try {
      if (currentResult) {
        sessionStorage.setItem('fibreg_active_protocol', JSON.stringify({
          input: currentInput,
          result: currentResult,
          timestamp: new Date().toISOString(),
        }));
      }
    } catch (e) {}

    const heroSection = document.getElementById('science-hero-section');
    const subsectionsSection = document.getElementById('science-subsections-section');
    const resultsSection = document.getElementById('science-results-section');

    if (!resultsSection) return;

    // 1. Hero Prescription Banner (Always 100% visible & readable for both visitors & customers)
    const heroDoseEl = document.getElementById('res-hero-dose');
    const heroDeficitTextEl = document.getElementById('res-hero-deficit-text');
    const adaptationHalfDoseEl = document.getElementById('res-adaptation-half-dose');
    const adaptationFullDoseEl = document.getElementById('res-adaptation-full-dose');
    const heroOrderTextEl = document.getElementById('res-hero-order-text');

    if (heroDoseEl) heroDoseEl.textContent = result.tier4.fiberGServingGrams + 'g';
    if (heroDeficitTextEl) heroDeficitTextEl.textContent = result.tier3.fiberDeficitGrams + 'g daily fiber deficit';
    if (adaptationHalfDoseEl) adaptationHalfDoseEl.textContent = Math.round(result.tier4.fiberGServingGrams / 2) + 'g (half dose)';
    if (adaptationFullDoseEl) adaptationFullDoseEl.textContent = result.tier4.fiberGServingGrams + 'g';
    if (heroOrderTextEl) heroOrderTextEl.textContent = 'Order Your ' + result.tier4.fiberGServingGrams + 'g Supply';

    // 2. Section 1: Biometric Baseline & Energy Engine
    const bmiValEl = document.getElementById('res-bmi-val');
    const bmiBadgeEl = document.getElementById('res-bmi-badge');
    const idealWeightValEl = document.getElementById('res-ideal-weight-val');
    const bmrValEl = document.getElementById('res-bmr-val');
    const tdeeValEl = document.getElementById('res-tdee-val');

    if (bmiValEl) bmiValEl.textContent = result.tier1.bmi;
    if (bmiBadgeEl) {
      bmiBadgeEl.textContent = result.tier1.bmiClassification;
      if (result.tier1.bmiClassification === 'Normal weight') {
        bmiBadgeEl.className = 'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-heading font-extrabold uppercase bg-[#dcfce7] text-[#006941] border border-[#86efac]/80';
      } else if (result.tier1.bmiClassification === 'Overweight') {
        bmiBadgeEl.className = 'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-heading font-extrabold uppercase bg-[#fef3c7] text-[#92400e] border border-[#fcd34d]';
      } else {
        bmiBadgeEl.className = 'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-heading font-extrabold uppercase bg-[#fee2e2] text-[#991b1b] border border-[#fca5a5]';
      }
    }
    if (idealWeightValEl) idealWeightValEl.textContent = result.tier1.idealBodyWeight;
    if (bmrValEl) bmrValEl.textContent = result.tier1.bmr;
    if (tdeeValEl) tdeeValEl.textContent = result.tier2.tdee;

    // 3. Section 2: Macronutrient Split & Health Gauges
    const macroFuelEl = document.getElementById('res-macro-fuel');
    const proteinGEl = document.getElementById('res-macro-protein-g');
    const proteinPctEl = document.getElementById('res-macro-protein-pct');
    const fatsGEl = document.getElementById('res-macro-fats-g');
    const fatsPctEl = document.getElementById('res-macro-fats-pct');
    const carbsGEl = document.getElementById('res-macro-carbs-g');
    const carbsPctEl = document.getElementById('res-macro-carbs-pct');
    const barProtein = document.getElementById('res-macro-bar-protein');
    const barFats = document.getElementById('res-macro-bar-fats');
    const barCarbs = document.getElementById('res-macro-bar-carbs');

    const totalCalories = result.tier2.calorieTarget || 2000;
    const proteinPct = Math.round(((result.tier3.proteinGrams * 4) / totalCalories) * 100);
    const fatPct = Math.round(((result.tier3.fatGrams * 9) / totalCalories) * 100);
    const carbsPct = Math.max(0, 100 - (proteinPct + fatPct));

    if (macroFuelEl) macroFuelEl.textContent = result.tier2.calorieTarget + ' kcal/day';
    if (proteinGEl) proteinGEl.textContent = result.tier3.proteinGrams + 'g';
    if (proteinPctEl) proteinPctEl.textContent = proteinPct + '% of cals';
    if (fatsGEl) fatsGEl.textContent = result.tier3.fatGrams + 'g';
    if (fatsPctEl) fatsPctEl.textContent = fatPct + '% of cals';
    if (carbsGEl) carbsGEl.textContent = result.tier3.carbGrams + 'g';
    if (carbsPctEl) carbsPctEl.textContent = carbsPct + '% of cals';

    if (barProtein) barProtein.style.width = proteinPct + '%';
    if (barFats) barFats.style.width = fatPct + '%';
    if (barCarbs) barCarbs.style.width = carbsPct + '%';

    // SVG Circular Gauge calculations (radius = 36, circumference = 226.19)
    const radius = 36;
    const circumference = 2 * Math.PI * radius;

    const gutScoreEl = document.getElementById('res-gut-score');
    const gutBadgeEl = document.getElementById('res-gut-badge');
    const gutCircle = document.getElementById('res-gut-circle');

    if (gutScoreEl) gutScoreEl.textContent = result.tier3.gutHealthScore;
    if (gutBadgeEl) {
      const isGood = result.tier3.gutHealthLabel === 'Good';
      const isModerate = result.tier3.gutHealthLabel === 'Moderate';
      const badgeBg = isGood ? 'bg-[#dcfce7] text-[#006941] border-[#86efac]/80' : isModerate ? 'bg-[#fef3c7] text-[#92400e] border-[#fcd34d]' : 'bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]';
      const dotBg = isGood ? 'bg-[#006941]' : isModerate ? 'bg-[#d97706]' : 'bg-[#dc2626]';
      gutBadgeEl.className = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-heading font-extrabold uppercase ' + badgeBg;
      gutBadgeEl.innerHTML = '<span class="w-1.5 h-1.5 rounded-full ' + dotBg + '"></span><span>' + result.tier3.gutHealthLabel + '</span>';
    }
    if (gutCircle) {
      const gutPct = Math.min(100, Math.max(0, result.tier3.gutHealthScore));
      const gutOffset = circumference - (gutPct / 100) * circumference;
      gutCircle.style.strokeDasharray = circumference;
      gutCircle.style.strokeDashoffset = gutOffset;
      gutCircle.style.stroke = result.tier3.gutHealthLabel === 'Good' ? '#006941' : result.tier3.gutHealthLabel === 'Moderate' ? '#f59e0b' : '#ef4444';
    }

    const metabolicScoreEl = document.getElementById('res-metabolic-score');
    const metabolicBadgeEl = document.getElementById('res-metabolic-badge');
    const metabolicTextEl = document.getElementById('res-metabolic-text');
    const metabolicCircle = document.getElementById('res-metabolic-circle');

    if (metabolicScoreEl) metabolicScoreEl.textContent = result.tier4.metabolicRiskScore;
    if (metabolicTextEl) metabolicTextEl.textContent = result.tier4.metabolicRiskMessage || 'Good baseline health';
    if (metabolicBadgeEl) {
      const isLow = result.tier4.metabolicRiskLevel === 'Low';
      const isModerate = result.tier4.metabolicRiskLevel === 'Moderate';
      const badgeBg = isLow ? 'bg-[#dcfce7] text-[#006941] border-[#86efac]/80' : isModerate ? 'bg-[#fef3c7] text-[#92400e] border-[#fcd34d]' : 'bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]';
      const dotBg = isLow ? 'bg-[#006941]' : isModerate ? 'bg-[#d97706]' : 'bg-[#dc2626]';
      metabolicBadgeEl.className = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-heading font-extrabold uppercase ' + badgeBg;
      metabolicBadgeEl.innerHTML = '<span class="w-1.5 h-1.5 rounded-full ' + dotBg + '"></span><span>' + result.tier4.metabolicRiskLevel + ' Risk</span>';
    }
    if (metabolicCircle) {
      const metPct = Math.min(100, Math.max(0, (result.tier4.metabolicRiskScore / 10) * 100));
      const metOffset = circumference - (metPct / 100) * circumference;
      metabolicCircle.style.strokeDasharray = circumference;
      metabolicCircle.style.strokeDashoffset = metOffset;
      metabolicCircle.style.stroke = result.tier4.metabolicRiskLevel === 'Low' ? '#006941' : result.tier4.metabolicRiskLevel === 'Moderate' ? '#f59e0b' : '#ef4444';
    }

    // Deficit Gap Alert Box
    const alertDeficitEl = document.getElementById('res-alert-deficit');
    const alertTargetEl = document.getElementById('res-alert-target');
    const alertTypicalEl = document.getElementById('res-alert-typical');
    const alertDeficitLargeEl = document.getElementById('res-alert-deficit-large');

    if (alertDeficitEl) alertDeficitEl.textContent = result.tier3.fiberDeficitGrams + 'g';
    if (alertTargetEl) alertTargetEl.textContent = result.tier2.fiberTargetGrams + 'g';
    if (alertTypicalEl) alertTypicalEl.textContent = Math.round(result.tier2.fiberTargetGrams - result.tier3.fiberDeficitGrams) + 'g';
    if (alertDeficitLargeEl) alertDeficitLargeEl.textContent = '-' + result.tier3.fiberDeficitGrams + 'g';

    // 4. Section 3: Clinical AI Recommendations
    const aiGridEl = document.getElementById('res-ai-recommendations-grid');
    if (aiGridEl) {
      aiGridEl.innerHTML = '';
      
      const waterLiters = (result.tier1 && result.tier1.idealBodyWeight) 
        ? Math.max(2.5, (result.tier1.idealBodyWeight * 0.045)).toFixed(1) 
        : '3.3';

      const recs = [
        'Your daily fiber target is ' + result.tier2.fiberTargetGrams + 'g. Start with ' + result.tier4.fiberGServingGrams + 'g of Fibre-G and increase gradually over 2 weeks.',
        'Drink at least ' + waterLiters + 'L of water daily to support fiber digestion.'
      ];

      if (result.aiRecommendations && result.aiRecommendations.length > 0) {
        result.aiRecommendations.forEach(function (rec) {
          if (!recs.includes(rec)) {
            recs.push(rec);
          }
        });
      }

      recs.forEach(function (recText, idx) {
        const card = document.createElement('div');
        card.className = 'bg-white rounded-2xl p-5 border border-[#e2e8f0] shadow-xs hover:border-[#006941]/30 transition-all flex items-start gap-4';
        card.innerHTML = 
          '<div class="w-8 h-8 rounded-xl bg-[#ecfdf5] text-[#006941] font-heading font-black text-sm flex items-center justify-center shrink-0 mt-0.5">' +
            (idx + 1) +
          '</div>' +
          '<p class="font-sans text-xs sm:text-sm text-[#2c2f2e] leading-relaxed m-0 font-medium">' +
            recText +
          '</p>';
        aiGridEl.appendChild(card);
      });
    }

    // 5. Bottom Order Button
    const orderBottomTextEl = document.getElementById('res-order-bottom-text');
    if (orderBottomTextEl) {
      orderBottomTextEl.textContent = 'Order Your ' + result.tier4.fiberGServingGrams + 'g Protocol Supply';
    }

    // 6. Check Auth State and Update Blur / Unlock Overlay
    syncResultsLockState();

    // Toggle views
    if (heroSection) heroSection.classList.add('hidden');
    if (subsectionsSection) subsectionsSection.classList.add('hidden');
    resultsSection.classList.remove('hidden');

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function syncResultsLockState() {
    const isAuthed = window.FibregAuth && window.FibregAuth.isAuthenticated();
    const lockedContent = document.getElementById('protocol-locked-content');
    const unlockOverlay = document.getElementById('protocol-unlock-overlay');

    if (lockedContent && unlockOverlay) {
      if (isAuthed) {
        // Authenticated: reveal all metrics and remove blur
        lockedContent.classList.remove('protocol-blurred');
        unlockOverlay.classList.add('hidden');
        unlockOverlay.style.display = 'none';

        // Auto-save to PostgreSQL if not saved yet
        if (currentInput && window.FibregAuth && window.FibregAuth.saveProtocolSubmission) {
          window.FibregAuth.saveProtocolSubmission(currentInput, currentResult?.id).then(function (savedData) {
            if (savedData && savedData.id && currentResult) {
              currentResult.id = savedData.id;
            }
          });
        }
      } else {
        // Logged-out: blur details below hero card and display unlock overlay
        lockedContent.classList.add('protocol-blurred');
        unlockOverlay.classList.remove('hidden');
        unlockOverlay.style.display = 'flex';
      }
    }
  }

  function resetView() {
    const heroSection = document.getElementById('science-hero-section');
    const subsectionsSection = document.getElementById('science-subsections-section');
    const resultsSection = document.getElementById('science-results-section');

    if (resultsSection) resultsSection.classList.add('hidden');
    if (heroSection) heroSection.classList.remove('hidden');
    if (subsectionsSection) subsectionsSection.classList.remove('hidden');

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function init() {
    // Listen for auth status changes
    window.addEventListener('fibreg:auth:change', function (e) {
      syncResultsLockState();
    });

    const unlockBtn = document.getElementById('btn-protocol-login-unlock');
    if (unlockBtn) {
      unlockBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
          window.FibregAuth.openVerificationModal();
        } else {
          window.location.href = window.FibregTheme?.routes?.account_login_url || '/account/login';
        }
      });
    }

    const form = document.getElementById('protocol-form');
    if (!form) return;

    // State holders
    let selectedGoal = 'maintenance';
    let selectedBodyType = 'average';
    let selectedEating = 'normal';
    let selectedActivity = 'standing';
    let selectedConditions = [];

    // Goal buttons
    const goalBtns = document.querySelectorAll('[data-goal-val]');
    goalBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectedGoal = btn.getAttribute('data-goal-val');
        goalBtns.forEach(function (b) {
          b.classList.remove('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
          b.classList.add('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
        btn.classList.remove('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
        btn.setAttribute('aria-checked', 'true');
      });
    });

    // Body Type buttons
    const bodyBtns = document.querySelectorAll('[data-body-val]');
    bodyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectedBodyType = btn.getAttribute('data-body-val');
        bodyBtns.forEach(function (b) {
          b.classList.remove('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
          b.classList.add('bg-[#f0f2f0]', 'border-transparent', 'text-[#595c5b]');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
        btn.classList.remove('bg-[#f0f2f0]', 'border-transparent', 'text-[#595c5b]');
        btn.setAttribute('aria-checked', 'true');
      });
    });

    // Eating Level buttons
    const eatingBtns = document.querySelectorAll('[data-eating-val]');
    eatingBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectedEating = btn.getAttribute('data-eating-val');
        eatingBtns.forEach(function (b) {
          b.classList.remove('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
          b.classList.add('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
        btn.classList.remove('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
        btn.setAttribute('aria-checked', 'true');
      });
    });

    // Occupational Activity buttons
    const actBtns = document.querySelectorAll('[data-act-val]');
    actBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectedActivity = btn.getAttribute('data-act-val');
        actBtns.forEach(function (b) {
          b.classList.remove('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
          b.classList.add('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('bg-[#eef7f2]', 'border-[#1e6b37]', 'text-[#1e6b37]');
        btn.classList.remove('bg-[#f0f2f0]', 'border-transparent', 'text-[#2c2f2e]');
        btn.setAttribute('aria-checked', 'true');
      });
    });

    // Condition pills
    const condBtns = document.querySelectorAll('[data-cond-val]');
    condBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const val = btn.getAttribute('data-cond-val');
        const idx = selectedConditions.indexOf(val);
        if (idx !== -1) {
          selectedConditions.splice(idx, 1);
          btn.classList.remove('bg-[#1e6b37]', 'border-[#1e6b37]', 'text-white', 'shadow-sm');
          btn.classList.add('bg-white', 'border-[#d2d6d2]', 'text-[#2c2f2e]');
          btn.setAttribute('aria-pressed', 'false');
        } else {
          selectedConditions.push(val);
          btn.classList.add('bg-[#1e6b37]', 'border-[#1e6b37]', 'text-white', 'shadow-sm');
          btn.classList.remove('bg-white', 'border-[#d2d6d2]', 'text-[#2c2f2e]');
          btn.setAttribute('aria-pressed', 'true');
        }
      });
    });

    // Form submission
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      const ageInput = document.getElementById('input-age');
      const genderInput = document.getElementById('select-gender');
      const weightInput = document.getElementById('input-weight');
      const heightInput = document.getElementById('input-height');
      const notesInput = document.getElementById('user-notes');
      const submitBtn = document.getElementById('calc-submit-btn');

      const age = parseInt(ageInput.value, 10);
      const gender = genderInput.value;
      const weight = parseFloat(weightInput.value);
      const height = parseFloat(heightInput.value);
      const notes = notesInput ? notesInput.value : '';

      const calculationPayload = {
        age: age,
        gender: gender,
        weight: weight,
        height: height,
        primaryGoal: selectedGoal,
        bodyType: selectedBodyType,
        eatingLevel: selectedEating,
        activityLevel: selectedActivity,
        healthConditions: selectedConditions,
        notes: notes,
      };

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Calculating Precision Intake...';
      }

      try {
        const result = await calculateProtocol(calculationPayload);
        renderResults(result, calculationPayload);
      } catch (err) {
        console.error('Calculation failed:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Calculate Precision Intake';
        }
      }
    });

    // Back / Retake / Recalculate buttons
    const backBtn = document.getElementById('btn-retake-protocol');
    if (backBtn) {
      backBtn.addEventListener('click', resetView);
    }
    const recalcBottomBtn = document.getElementById('btn-recalculate-bottom');
    if (recalcBottomBtn) {
      recalcBottomBtn.addEventListener('click', resetView);
    }
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  return {
    computeLocalProtocol: computeLocalProtocol,
    calculateProtocol: calculateProtocol,
    renderResults: renderResults,
    syncResultsLockState: syncResultsLockState,
    resetView: resetView,
    init: init,
  };
})();
